import { listQueryAsResourceList, resourceListQuery } from "api/graphqlTypes";
import { Form } from "components/Form/components/Form/Form";
import PageContent from "components/Layout/PageContent";
import StateTabs from "components/StateTabs";
import RecordTable from "components/Table/RecordTable";
import { locationColumn } from "components/Table/recordTableUtils";
import {
    getInitialQueryWithTableSettings,
    getTableSettings,
    TableColumn,
} from "components/Table/tableUtils";
import { resources } from "config/resources";
import { FeConfigType } from "config/config";
import { moreTabList, shownTabList } from "config/resources/resourceTabs";
import { commonColumns } from "containers/AdvancedSearch/components/advancedFilterColumns";
import { ShowBundleContentFilter } from "containers/Filter/components/ShowDeletedFilter";
import InlineActionButtons from "containers/Resources/components/CommonActions/InlineActionButtons";
import { MetadataList } from "containers/Resources/screens/ResourcesSingle/components/MetadataList/MetadataList";
import {
    metadataTab,
    metadataTabKey,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { trackedInGitColumn, typeColumn } from "containers/Search/components/SearchTabs/columns";
import UploadFileButton, {
    FileUploadLocation,
} from "containers/Search/components/SearchTabs/UploadFileButton";
import { bootstrapUserNameSelector } from "containers/Workspaces/Workspaces.selectors";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { Form as FinalForm } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch } from "react-router-dom";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { sortAlphabetically } from "utils/sorting";
import { getFilterFieldsList, getSearchObj, mapResourceTab } from "utils/utils";
import "./SearchTabs.scss";

type SearchTabsProps = {
    filter?: any;
    allTabsFilter?: any;
    name: string;
    showLocationColumn: boolean;
    tableOptions?: any;
    additionalColumns?: TableColumn[];
    resourceKeyName: string;
    additionalQueryParameters?: {
        allow_normal?: boolean;
        allow_deleted?: boolean;
    };
    resourceRetentionTimeMinutes?: number;
    orderBy?: string;
    isProjectReadOnly?: boolean;
    type?: "record" | "resource";
    fetchSearchTabsRef?: MutableRefObject<(() => Promise<any>) | undefined>;
    id?: string;
    isRecordDisabled?: boolean;
    isGitEnabled?: boolean;
    isBundle?: boolean;
    hasUnsavedChanges?: boolean;
    setHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
    bootstrapUserName?: string;
};

/**
 * SearchTabs component provides a tabbed interface for searching and displaying resources
 * with filtering, sorting, and pagination capabilities.
 *
 * This component handles:
 * - Multiple resource type tabs (All, specific resource types, metadata)
 * - Search filtering and query management
 * - Table display with customizable columns
 *
 * @param props - SearchTabsProps object containing all component configuration
 * @returns JSX element representing the search tabs interface
 */
const SearchTabs = ({
    name,
    filter = null,
    showLocationColumn = true,
    tableOptions = {},
    additionalColumns = [],
    resourceKeyName,
    additionalQueryParameters = {},
    resourceRetentionTimeMinutes,
    orderBy,
    isProjectReadOnly,
    type = "resource",
    allTabsFilter = null,
    fetchSearchTabsRef,
    id,
    isRecordDisabled,
    isGitEnabled,
    isBundle,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    bootstrapUserName: bootstrapUserNameProp,
}: SearchTabsProps) => {
    const dispatch = useDispatch();

    const allTabKey = "all";
    const initialRowsPerPage = 30;
    const activeTabInitialQuery = { offset: 0, order: orderBy ? orderBy : "-modified_at" };

    /**
     * Generates a unique key for table settings based on resource and tab
     * @param tab - The current tab identifier
     * @returns Unique table settings key
     */
    const getTableSettingsTabKey = (tab) =>
        `search_tabs${
            resourceKeyName === "trashbin" ? resourceKeyName : `${resourceKeyName}-${tab}`
        }`;

    const [initialQuery, setInitialQuery] = useState({
        limit: initialRowsPerPage as typeof initialRowsPerPage,
        offset: 0,
        order: orderBy ? orderBy : "+name",
    });

    /**
     * Gets query with table settings applied for a specific tab
     * @param tab - The tab to get settings for
     * @returns Query object with table settings
     */
    const queryWithTableSettings = (tab) =>
        getInitialQueryWithTableSettings(getTableSettingsTabKey(tab), initialQuery);

    type TabKey = "all" | FeConfigType;
    const [isLoading, setIsLoading] = useState(false);
    const [queryInfo, setQueryInfo] = useState({
        query: queryWithTableSettings(allTabKey),
        tab: allTabKey as TabKey,
    });

    const params: { id?: string; name?: string } = useParams();
    const match = useRouteMatch();
    const resourceID = params?.id ?? params?.name;
    const resourceName = match?.path?.split("/")[1] || null;
    const key = `${resourceName}-${resourceID}-${name}`;

    const recordType = resourceName === "workspace" ? "record" : type;

    // Files tab upload: create FILE records directly in the container the tab is
    // scoped to. Only when we have a definite single-container context
    // (workspace root / a specific project / a specific bundle) — not on the
    // global search or other multi-container screens.
    const fileUploadLocation: FileUploadLocation | null =
        resourceName === "workspace"
            ? { project_id: null, bundle_id: null }
            : resourceName === "project" && id
              ? { project_id: id }
              : resourceName === "bundle" && id
                ? { bundle_id: id }
                : null;
    const showFileUpload =
        String(queryInfo.tab) === "file" &&
        !!fileUploadLocation &&
        !isProjectReadOnly &&
        !isRecordDisabled;
    const tabs = [
        { uid: allTabKey, label: "All" },
        ...((resourceName && shownTabList[resourceName]) ?? shownTabList.all).map(mapResourceTab),
    ];
    const moreTabs = ((resourceName && moreTabList[resourceName]) ?? moreTabList.all).map(
        mapResourceTab
    );
    const sortedMoreTabs = sortAlphabetically(
        [...moreTabs, ...(resourceKeyName === "trashBin" ? [] : [metadataTab.tab])],
        (type) => type.label
    );
    const searchResultsSelector = (state: any) =>
        state?.dataStore?.[key]?.[`${recordType}_list`] ?? [];
    const searchResults = useSelector(searchResultsSelector);
    const additionalResourceProps =
        resourceRetentionTimeMinutes !== undefined ? { resourceRetentionTimeMinutes } : {};
    const resourceList =
        searchResults?.[`${recordType}_list`]?.map((searchResult: any) => ({
            ...searchResult,
            ...additionalResourceProps,
            resource_type: searchResult?.record_type?.toLowerCase(),
        })) ?? [];
    const resourceCount = searchResults?.[`${recordType}_count`];

    const [additionalFilter, setAdditionalFilter] = useState<Array<any> | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    /**
     * Performs the search operation based on current query and filters
     * @returns Promise that resolves when search is complete
     */
    const performSearch = useCallback(() => {
        // Skip search for metadata tab
        if ((queryInfo.tab as TabKey | typeof metadataTabKey) === metadataTabKey) {
            return Promise.resolve({});
        }

        // Cancel any in-flight request for the previous tab/query.
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        setIsLoading(true);

        // Build filter array from various sources
        const filters: Array<any> = [];
        if (filter || (queryInfo.tab === allTabKey && allTabsFilter)) {
            filters.push(filter ?? allTabsFilter);
        }
        if (additionalFilter) {
            filters.push(...additionalFilter);
        }

        if (queryInfo.tab === allTabKey && resourceName === "workspace") {
            filters.push({
                field: "record_type",
                op: "in",
                value: [...shownTabList.workspace, ...moreTabList.workspace],
            });
        }
        const filterStr = filters ? JSON.stringify({ and: filters }) : null;

        return new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: dataStoreAction.START_FETCH_DATA,
                key,
                signal: abortControllerRef.current?.signal,
                params: {
                    query:
                        queryInfo.tab === allTabKey
                            ? resourceListQuery(recordType)
                            : listQueryAsResourceList(recordType, queryInfo.tab),
                    variables: {
                        ...queryInfo.query,
                        filter: filterStr,
                    },
                    ...additionalQueryParameters,
                },
                emptyListOnError: true,
            });
        }).finally(() => {
            setIsLoading(false);
        });
    }, [
        additionalFilter,
        additionalQueryParameters,
        allTabsFilter,
        dispatch,
        filter,
        key,
        queryInfo.query,
        queryInfo.tab,
        recordType,
        resourceName,
    ]);

    const performSearchRef = useRef<() => Promise<any>>(performSearch);

    const isMountedRef = useRef(true);

    // Create a custom refresh function that checks if component is still mounted
    const safeRefresh = useCallback(() => {
        if (isMountedRef.current) {
            // Component still mounted - calling refresh
            return performSearchRef.current();
        } else {
            // Component unmounted - skipping refresh
            return Promise.resolve();
        }
    }, []);

    useEffect(() => {
        performSearchRef.current = performSearch;
        if (fetchSearchTabsRef) {
            fetchSearchTabsRef.current = performSearch;
        }
    }, [fetchSearchTabsRef, performSearch, queryInfo]);

    useEffect(() => {
        performSearch();
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryInfo, additionalQueryParameters?.allow_deleted, isProjectReadOnly, additionalFilter]);

    // Track component mount state
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            // Component unmounting - setting isMounted to false
            isMountedRef.current = false;
            abortControllerRef.current?.abort();
        };
    }, []);
    /**
     * Updates the query state with new parameters
     * @param queryChange - Object containing query changes
     * @param tab - The tab to update the query for
     */
    const updateQuery = useCallback(
        (queryChange: any, tab: TabKey) => {
            const query = queryInfo.query;
            const merged = {
                ...query,
                ...queryChange,
            };
            setQueryInfo({ query: merged, tab: tab ? tab : allTabKey });
        },
        [queryInfo.query]
    );

    const updateQueryViaTable = useCallback(
        (change) => updateQuery(change, queryInfo.tab),
        [queryInfo.tab, updateQuery]
    );
    useEffect(() => {
        if (resourceCount > 0 && !resourceList.length && queryInfo.query.offset) {
            updateQuery({ offset: 0 }, queryInfo.tab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceList.length, resourceCount, queryInfo.query.offset]);

    const mappedColumns = Object.entries(commonColumns)
        .filter(([key, _]) => key !== "name")
        .map(([_, value]) => (value as any)());

    const bootstrapUserName =
        useSelector((state) =>
            bootstrapUserNameProp ? bootstrapUserNameSelector(state) : undefined
        ) || bootstrapUserNameProp;

    const baseColumns: TableColumn[] = [
        commonColumns.name({ dispatch, bootstrapUserName }),
        ...mappedColumns,
        ...additionalColumns,
    ];

    const actionsColumn: TableColumn = {
        label: "Actions",
        key: "actions",
        shown: true,
        sortable: false,
        dto: (value: any) => {
            return <InlineActionButtons resourceData={value} afterButtonAction={safeRefresh} />;
        },
    };

    /**
     * Gets the appropriate columns for a specific tab
     * @param tab - The tab to get columns for
     * @returns Array of table columns
     */
    const getColumns = useCallback((tab: string) => {
        // Return empty array for metadata tab
        if (tab === metadataTabKey) {
            return [];
        }

        let selectedColumns = Array.from(baseColumns);
        const selectedResource: any = resources.find((r) => r.resourceName === tab);
        if (showLocationColumn) {
            selectedColumns.splice(1, 0, locationColumn());
        }
        if (tab !== allTabKey) {
            const additionalColumns = selectedResource?.additionalColumns;
            if (additionalColumns) selectedColumns.push(...additionalColumns());
        } else {
            selectedColumns.splice(1, 0, typeColumn(recordType));
        }
        if (
            resourceKeyName !== "trashBin" &&
            (tab === allTabKey || selectedResource.actions.shown.length > 0)
        ) {
            selectedColumns.push(actionsColumn);
        }
        if (isGitEnabled && resourceKeyName !== "trashBin") {
            selectedColumns.push(trackedInGitColumn());
        }

        return selectedColumns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [columns, setColumns] = useState(getColumns(queryInfo.tab));

    useEffect(() => {
        setColumns(getColumns(queryInfo.tab));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryInfo.tab]);

    const tableSettingsKey = getTableSettingsTabKey(queryInfo.tab);

    return (
        <PageContent className="page-content searchTabs">
            {/* Tab navigation component */}
            <StateTabs
                activeTab={queryInfo.tab}
                setActiveTab={(tab) => {
                    const isCurrentTab = queryInfo.tab === tab;

                    // Switching tabs: cancel any in-flight request and clear rows immediately.
                    abortControllerRef.current?.abort();
                    dispatch({ type: dataStoreAction.STORE_DATA, key, data: null });

                    // Get table settings for the new tab
                    const changedOrder = getTableSettings(
                        getTableSettingsTabKey(tab),
                        "order",
                        isCurrentTab ? queryInfo.query.order : activeTabInitialQuery.order
                    );
                    const changedLimit = getTableSettings(
                        getTableSettingsTabKey(tab),
                        "limit",
                        queryInfo.query.limit
                    );
                    const activeTabQuery = {
                        ...activeTabInitialQuery,
                        order: changedOrder,
                        limit: changedLimit,
                    };
                    updateQuery(activeTabQuery, tab);
                    setInitialQuery({ ...queryInfo.query, ...activeTabQuery });
                }}
                isDisabled={hasUnsavedChanges}
                title={null}
                isConnectionType={false}
                tabs={tabs}
                hiddenTabs={sortedMoreTabs}
            />

            {/* Render metadata list for metadata tab */}
            {(queryInfo.tab as TabKey | typeof metadataTabKey) === metadataTabKey && id ? (
                <MetadataList
                    id={id}
                    isDisabled={isRecordDisabled ?? false}
                    setHasUnsavedChanges={
                        setHasUnsavedChanges as (hasUnsavedChanges: boolean) => void
                    }
                    resourceName={resourceKeyName as FeConfigType}
                />
            ) : (
                <>
                    {/* Bundle content filter form */}
                    {isBundle && (
                        <FinalForm
                            onSubmit={(formData, form) => {
                                setAdditionalFilter(() => {
                                    const showBundleContentField = getSearchObj({
                                        value: true,
                                        field: "is_bundle_content",
                                        op: "eq",
                                    });
                                    const showNonBundleContentField = getSearchObj({
                                        value: false,
                                        field: "is_bundle_content",
                                        op: "eq",
                                    });
                                    if (
                                        (formData.showBundleContent &&
                                            formData.showNonBundleContent) ||
                                        (!formData.showBundleContent &&
                                            !formData.showNonBundleContent)
                                    ) {
                                        form.change("showBundleContent", true);
                                        form.change("showNonBundleContent", true);
                                        return [
                                            {
                                                or: getFilterFieldsList(
                                                    showBundleContentField,
                                                    showNonBundleContentField
                                                ),
                                            },
                                        ];
                                    } else {
                                        if (formData.showBundleContent) {
                                            return [showBundleContentField];
                                        } else {
                                            return [showNonBundleContentField];
                                        }
                                    }
                                });
                            }}
                            initialValues={{ showBundleContent: true, showNonBundleContent: true }}
                        >
                            {({ handleSubmit, values }) => {
                                return (
                                    <Form onSubmit={handleSubmit} className="searchTabsFilter">
                                        <ShowBundleContentFilter
                                            onBlur={handleSubmit}
                                            showBundleContent={values.showBundleContent}
                                            showNonBundleContent={values.showNonBundleContent}
                                        />
                                    </Form>
                                );
                            }}
                        </FinalForm>
                    )}

                    {/* Main data table */}
                    <RecordTable
                        data={resourceList}
                        totalItemCount={resourceCount}
                        columns={columns}
                        isLoading={isLoading}
                        recordTypes={queryInfo.tab}
                        updateQuery={updateQueryViaTable}
                        refresh={safeRefresh}
                        options={{
                            density: "high",
                            currentOffset: queryInfo.query.offset,
                            // Resource list tabs fetch once and do not live-update; give the
                            // user a manual refresh. (operator-task-recordtable-manual-refresh-button)
                            showRefreshButton: true,
                            ...tableOptions,
                            ...(showFileUpload
                                ? {
                                      // Upload sits with the row-action buttons on the
                                      // LEFT (before Move), not with the table-display
                                      // controls on the right (Margot, 2026-08-18).
                                      customLeftToolbarChildren: (
                                          <UploadFileButton
                                              location={
                                                  fileUploadLocation as FileUploadLocation
                                              }
                                              onUploaded={safeRefresh}
                                          />
                                      ),
                                  }
                                : {}),
                        }}
                        initialQuery={queryWithTableSettings(queryInfo.tab)}
                        tableSettingsKey={tableSettingsKey}
                        isContainerGitEnabled={isGitEnabled}
                        isBundle={isBundle}
                        className="stickyFooterTable"
                    />
                </>
            )}
        </PageContent>
    );
};

export default SearchTabs;
